<!-- Says, permanently, that this is not your data. Factory-owned.

     A COMPACT STRIP, not a toolbar. It used to be a stock `IonToolbar`, which
     was the right instinct — no bespoke styling, inherit whatever the platform
     and palette decide — and the wrong result. A toolbar is 56px on iOS and 62
     on Material because it is built to hold a title and two rows of buttons,
     and on a phone with a Dynamic Island it sits on top of a ~59px safe-area
     inset as well. Measured on a real device: 137pt of a 458pt-wide screen,
     before the app's own header had drawn anything. Roughly a seventh of the
     first screenful, permanently, on the one surface the store screenshots are
     taken from.

     This is one line of small text with tight padding — about 24px plus the
     inset it genuinely has to clear. Same words, same permanence, same
     colour; a quarter of the height. The colour still comes from the palette
     (`--ion-color-warning`), so an app that rebrands takes the banner with it.

     NOT DISMISSIBLE, on purpose. Somebody looking at demo data and believing it
     is theirs is the failure this exists to prevent, and a banner you can close
     is a banner that is closed.

     THE LINK inherits the strip's own foreground rather than the browser's blue.
     A bare `<a>` measures 5.89:1 on the warning colour — legible, so this is not
     a contrast fix — but it is the only element on screen wearing a colour
     nothing else in the app uses. `currentColor` takes the foreground instead,
     at 13.15:1, and the underline keeps it obviously a link.

     Target size: an inline link inside a sentence is explicitly exempt from
     WCAG 2.5.8, which is what lets this be one line rather than a button. -->
<template>
  <div ref="el" class="demo-banner">
    <p>
      Demo data — nothing is saved.
      <a href="/app">Open the real app</a>
    </p>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';

const el = ref(null);
let observer = null;

/**
 * Make room for this banner.
 *
 * `ion-router-outlet` is `position: absolute; inset: 0`, so it does NOT flow
 * below a sibling — it fills the whole app and paints the page's own
 * `ion-header` at y=0, exactly where this banner already is. The banner is
 * later in paint order, so it wins, and the app's toolbar is invisible for as
 * long as demo mode is on.
 *
 * That is worse than it sounds. `/demo` is the surface the store screenshots
 * are taken from and the surface QA audits, so every picture of every app this
 * factory has produced lost its toolbar — the title, the settings button, the
 * help button — and nothing errored. It is only wrong in demo mode, which is
 * the one mode nobody develops in.
 *
 * Measured rather than hardcoded: the strip grows when the text wraps on a
 * narrow screen, and it grows a lot at 200% reading size.
 */
function measure() {
  const host = el.value?.$el ?? el.value;
  if (!host) return;
  const h = Math.ceil(host.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--demo-banner-h', `${h}px`);
}

onMounted(() => {
  measure();
  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(measure);
    const host = el.value?.$el ?? el.value;
    if (host) observer.observe(host);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  document.documentElement.style.removeProperty('--demo-banner-h');
});
</script>

<!-- Not scoped: the last rule has to reach ion-router-outlet, which is not this
     component's element. The variable is unset in the real app, so the
     fallback of 0 makes that rule inert everywhere except demo mode. -->
<style>
.demo-banner {
  position: relative;
  z-index: 2;
  /* The strip paints BEHIND the status bar rather than starting below it: a
     white band above a yellow one reads as a rendering fault, and the inset is
     the app's to fill. This padding is most of the banner's height on a
     Dynamic Island phone and there is nothing to be saved there. */
  padding-top: env(safe-area-inset-top, 0px);
  background: var(--ion-color-warning, #ffc409);
  color: var(--ion-color-warning-contrast, #000000);
}

.demo-banner p {
  margin: 0;
  padding: 5px var(--sp-4, 16px);
  text-align: center;
  /* A literal rem rather than `--text-xs`, because this component ships in
     every app and the recipes move that token. The banner should be the same
     size in all of them — it is the chassis talking, not the product. */
  font-size: 0.75rem;
  line-height: 1.35;
}

/* Inherit the strip's foreground rather than the browser's link blue. */
.demo-banner a {
  color: currentColor;
  text-decoration-thickness: from-font;
  text-underline-offset: 0.15em;
}

ion-app > ion-router-outlet {
  top: var(--demo-banner-h, 0);
}
</style>
